import React from 'react';

interface AppIconProps {
  size?: number;
  isSticky?: boolean;
  showPlaceholder?: boolean;
  className?: string;
}

function AppIcon({ 
  size = 180, 
  isSticky = false, 
  showPlaceholder = false,
  className = ""
}: AppIconProps) {
  const [scrollProgress, setScrollProgress] = React.useState(0);
  const [isDarkTheme, setIsDarkTheme] = React.useState(false);

  React.useEffect(() => {
    if (!isSticky) return;
    
    const handleScroll = () => {
      const scrollPosition = window.scrollY;
      const maxScroll = 300; // Distance over which to complete the transformation
      const progress = Math.min(Math.max(scrollPosition / maxScroll, 0), 1);
      setScrollProgress(progress);
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, [isSticky]);

  // Check initial theme preference
  React.useEffect(() => {
    const checkColorScheme = () => {
      const prefersDark = window.matchMedia('(prefers-color-scheme: dark)').matches;
      setIsDarkTheme(prefersDark);
      
      // Set initial theme classes
      if (prefersDark) {
        document.documentElement.classList.add('dark-theme');
        document.documentElement.classList.remove('light-theme');
      } else {
        document.documentElement.classList.add('light-theme');
        document.documentElement.classList.remove('dark-theme');
      }
    };

    // Set initial state
    checkColorScheme();

    // Listen for changes
    const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');
    mediaQuery.addEventListener('change', checkColorScheme);

    // Cleanup
    return () => mediaQuery.removeEventListener('change', checkColorScheme);
  }, []);

  // Calculate icon size based on scroll progress (only for sticky icons)
  const iconSize = isSticky ? 180 - (scrollProgress * 110) : size; // From 180px to 90px
  
  // Calculate blur area size based on scroll progress for smoother transitions
  const blurPadding = isSticky ? 12 - (scrollProgress * 3) : 12; // From 20px to 15px
  const blurRadius = iconSize * 0.233; // From 50px to 40px

  const handleIconClick = () => {
    if (isSticky) {
      // Toggle theme instead of scrolling up
      const newTheme = !isDarkTheme;
      setIsDarkTheme(newTheme);
      
      // Apply theme to document
      if (newTheme) {
        document.documentElement.classList.add('dark-theme');
        document.documentElement.classList.remove('light-theme');
      } else {
        document.documentElement.classList.add('light-theme');
        document.documentElement.classList.remove('dark-theme');
      }
    }
  };

  return (
    <>
      {/* Icon Container */}
      <div 
        className={`hero-icon ${isSticky ? 'sticky' : ''} ${className}`}
        style={{
          padding: `${blurPadding}px`,
          borderRadius: `${blurRadius}px`,
          transform: isSticky ? 'translateX(-50%)' : 'none',
          left: isSticky ? '50%' : 'auto',
          position: isSticky ? 'fixed' : 'static',
          zIndex: isSticky ? 1000 : 'auto',
          top: isSticky ? '20px' : 'auto'
        }}
      >
        <div 
          className="icon-clickable"
          onClick={handleIconClick}
          style={{ cursor: isSticky ? 'pointer' : 'default' }}
        >
          <img
            src="/icon.png"
            alt="ControllerApp Icon"
            className="app-icon"
            style={{
              width: `${iconSize}px`,
              height: `${iconSize}px`,
              borderRadius: `${iconSize * 0.233}px`, // Maintains proportion (42/180 = 0.233)
            }}
          />
        </div>
      </div>
      
      {/* Placeholder to prevent content jump since icon is always sticky */}
      {showPlaceholder && (
        <div 
          className="icon-placeholder"
          style={{
            width: '180px',
            height: '180px',
            marginBottom: '2rem'
          }}
        />
      )}
    </>
  );
}

export default AppIcon;
